import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { PACKAGING_LEVEL_STAGES, PRODUCT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { CREATE, MODIFY, PACKAGING, PRODUCT, VIEW, useFoodpilotContext } from "src/context/FoodpilotContext";

const PRODUCT_FORM_TRANSPORT_STAGES = [...PRODUCT_LEVEL_STAGES, ...PACKAGING_LEVEL_STAGES];

/**
 * Combines org-scoped RBAC with application-parameter feature flags for product lifecycle steps.
 */
export const useProductLifecycleStepGate = () => {
    const { canUser } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const { isStageEnabled } = useEnabledTransportStages();

    const canViewProduct = canUser(VIEW, PRODUCT);
    const canModifyProduct = canUser(MODIFY, PRODUCT);
    const canViewPackaging = canUser(VIEW, PACKAGING);
    const canCreatePackaging = canUser(CREATE, PACKAGING);
    const canModifyPackaging = canUser(MODIFY, PACKAGING);

    return {
        canViewProduct,
        canModifyProduct,
        canViewPackaging,
        canCreatePackaging,
        canModifyPackaging,
        isRecipeEnabled: isFeatureActivated("FS-entity-ingredients") && canModifyProduct,
        isTransformationEnabled: isFeatureActivated("FS-product-transformation") && canModifyProduct,
        isPackagingEnabled: isFeatureActivated("FS-entity-packagings") && canModifyProduct,
        isTransportEnabled:
            isFeatureActivated("FS-product-transport") &&
            PRODUCT_FORM_TRANSPORT_STAGES.some((stage) => isStageEnabled(stage)) &&
            canModifyProduct,
        isDistributionEnabled: isFeatureActivated("FS-product-distribution") && canModifyProduct,
        isConsumptionEnabled: isFeatureActivated("FS-product-consumption") && canModifyProduct,
        isRecipeVisible: isFeatureActivated("FS-entity-ingredients") && (canViewProduct || canModifyProduct),
        isTransformationVisible:
            isFeatureActivated("FS-product-transformation") && (canViewProduct || canModifyProduct),
        isPackagingVisible: isFeatureActivated("FS-entity-packagings") && (canViewProduct || canModifyProduct),
        isTransportVisible:
            isFeatureActivated("FS-product-transport") &&
            PRODUCT_FORM_TRANSPORT_STAGES.some((stage) => isStageEnabled(stage)) &&
            (canViewProduct || canModifyProduct),
        isDistributionVisible: isFeatureActivated("FS-product-distribution") && (canViewProduct || canModifyProduct),
        isConsumptionVisible: isFeatureActivated("FS-product-consumption") && (canViewProduct || canModifyProduct),
    };
};
